import { CSSResultGroup, html, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ValueFormElement } from 'elements/_shared/formElement.js'
import { CSEOption, CSEOptionClickedEvent } from 'elements/option/option.js'
import { MultiSelectStrategy, SelectStrategy, SingleSelectStrategy } from './select-strategies.js'
import { style } from './select.style.js'
import { formFieldValidationTypes } from 'elements/form-field/form-field.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'


const selectStrategyMap = {
  single: SingleSelectStrategy,
  multi: MultiSelectStrategy,
}

@customElement('cse-select')
export class CSESelect extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property()
  name = ''

  @property({ type: String, reflect: true })
  value = ''

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  required = false

  @property({ type: Boolean })
  override autofocus = false

  @property({ type: String })
  placeholder = ''

  @property({ type: Boolean, reflect: true })
  active = false

  @property({ attribute: 'max-options-visible', type: Number })
  maxOptionsVisible = 3

  @property({ type: String, attribute: 'select-type' })
  selectType: 'single' | 'multi' = 'single'

  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledBy?: string | null

  @property({ type: String })
  valid: formFieldValidationTypes[number] = 'none'

  @state()
  options!: CSEOption[]

  private dropdownDistance = 5

  override connectedCallback(): void {
    super.connectedCallback()
    this.setOptions()
    if (this.autofocus) {
      this.active = true
    }
  }

  override focus() {
    this.active = true
  }

  clearSelection = () => {
    if (this.disabled) { return }
    this.strategy.clearSelection()
  }

  selectByOption = (option: CSEOption) => {
    if (this.disabled) { return }
    this.strategy.selectByOption(option)
  }

  selectByValue = (value: string) => {
    if (this.disabled) { return }
    this.strategy.selectByValue(value)
  }

  private strategy!: SelectStrategy

  private addOpenListeners() {
    document.addEventListener('click', this.handleDocumentClick)
  }

  private removeOpenListeners() {
    document.removeEventListener('click', this.handleDocumentClick)
  }

  private updateValue = (value: string) => {
    if (this.value === value) { return }

    this.value = value
    this.requestUpdate()
    this.emit(value)
  }

  private toggleDropdown() {
    if (this.disabled) { return }

    this.active = !this.active
  }

  private handleDocumentClick = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) { this.toggleDropdown() }
  }

  private handleClearButtonClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (this.disabled) { return }
    this.strategy.clearSelection()
  }

  private handleOptionClick = (event: CSEOptionClickedEvent) => {
    if (this.disabled) { return }
    this.strategy.selectByOption(event.option)
    if (this.strategy.shouldToggleDropdown) { this.toggleDropdown() }
  }

  private handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === ' ') {
      event.preventDefault()
      this.toggleDropdown()
      if (this.active) {
        const option = this.shadowRoot?.querySelector('cse-option')
        if (option && 'updateComplete' in option) {
          await (option as any).updateComplete
        }
      }
    }
  }

  shouldRenderClearButton() {
    if(this.disabled) {
      return false
    }

    if (this.selectType === 'multi') {
      return this.value !== ''
    } else {
      return this.value !== '' && !this.required
    }
  }

  private renderPlaceholder = () => html`
    <div class="placeholder">${this.placeholder}</div>
  `

  private renderClearButton = () => html`
    <cse-icon-button
      variant="secondary"
      size="small"
      @click="${this.handleClearButtonClick}"
      aria-label="${t('clear_selection', { ns: 'cse-select', default: 'Clear selection' })}"
    >
      <cse-icon icon-role="cse-select.clear" aria-hidden="true"></cse-icon>
    </cse-icon-button>
  `

  override render(): TemplateResult {
    const classes: { [className: string]: boolean } = {
      'form-field': true,
      'disabled': this.disabled,
      'active': this.active,
      [`valid-${this.valid}`]: true,
      ...(this.selectType && { [this.selectType]: true })
    }
    return html`
      <cse-popup
        class="cse-select"
        ?active="${this.active}"
        position="bottom"
        distance="${this.dropdownDistance}"
        sync="width"
        auto-size="vertical"
        auto-size-padding="15"
      >
        <div
          slot="anchor"
          class="${classMap(classes)}"
          ?inert="${this.disabled}"
          @click="${this.toggleDropdown}"
          @keydown=${this.handleKeyDown}
          aria-labelledby=${ifDefined(this.ariaLabelledBy ?? undefined)}
          tabindex="0"
        >
          ${this.value !== '' ? this.strategy.renderSelection(this.disabled) : this.renderPlaceholder()}
          <div class="actions">
            ${this.shouldRenderClearButton() ? this.renderClearButton() : null}
            <cse-icon class="arrow" icon-role="cse-select.arrow" aria-hidden="true"></cse-icon>
          </div>
        </div>

        <cse-dropdown-menu>
          <slot
            class="options"
            @slotchange="${this.setOptions}"
            @cse-option-clicked="${this.handleOptionClick}"
          ></slot>
        </cse-dropdown-menu>
      </cse-popup>
    `
  }

  setOptions() {
    this.options = Array.from(this.querySelectorAll('cse-option'))
    this.strategy = new selectStrategyMap[this.selectType](this.options, this.updateValue, this.maxOptionsVisible)
    this.strategy.selectByValue(this.value)
  }

  protected override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties)

    if (this.disabled) { return }

    if (changedProperties.has('value') && this.value !== changedProperties.get('value')) {
      this.strategy.selectByValue(this.value)
    }
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (this.disabled) { return }

    if (changedProperties.has('active')) {
      if (this.active) {
        this.addOpenListeners()
      } else {
        this.removeOpenListeners()
      }
    }
  }
}

initializeIconsByRole({
  'cse-select.arrow': { name: 'angle-down', style: 'light' },
  'cse-select.clear': { name: 'xmark', style: 'light' },
})

declare global {
  interface HTMLElementTagNameMap {
    'cse-select': CSESelect
  }
}
